import React from "react";

import FormFieldLabel from "../../../../components/common/FormFieldLabel";

import "../../../../css/cards.css";
import "../../../../css/forms.css";
import "../../../../css/buttons.css";

export interface HouseholdMember {
  id: string;
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  gender?: string;
  relationshipTypeId?: number;
}

export interface YouthProfile {
  schoolName: string;
  schoolYear: string;
  isLookedAfterChild: boolean | null;
  knownToSocialServices: boolean | null;
  hasEHCP: boolean | null;
  underCAMHS: boolean | null;
  medicalInformation: string;
  additionalNeeds: string;
  dietaryRequirements: string;
  gpName: string;
  gpContactNumber: string;
}

interface YouthProfileCardProps {
  compact?: boolean;
  child: HouseholdMember;
  value: YouthProfile;
  onChange: (value: YouthProfile) => void;
}

const schoolYears = [
  "Reception",
  "Year 1",
  "Year 2",
  "Year 3",
  "Year 4",
  "Year 5",
  "Year 6",
  "Year 7",
  "Year 8",
  "Year 9",
  "Year 10",
  "Year 11",
  "Year 12",
  "Year 13",
  "Other",
];

export const emptyYouthProfile = (): YouthProfile => ({
  schoolName: "",
  schoolYear: "",
  isLookedAfterChild: null,
  knownToSocialServices: null,
  hasEHCP: null,
  underCAMHS: null,
  medicalInformation: "",
  additionalNeeds: "",
  dietaryRequirements: "",
  gpName: "",
  gpContactNumber: "",
});

const getDisplayName = (child: HouseholdMember) =>
  `${child.firstName} ${child.lastName}`.trim() || "Young Person";

const getAge = (dateOfBirth: string) => {
  if (!dateOfBirth) return null;

  const today = new Date();
  const dob = new Date(`${dateOfBirth}T00:00:00`);
  let age = today.getFullYear() - dob.getFullYear();
  const monthDelta = today.getMonth() - dob.getMonth();

  if (monthDelta < 0 || (monthDelta === 0 && today.getDate() < dob.getDate())) {
    age--;
  }

  return age;
};

const lookedAfterChildHelpText =
  "A child who has been in the care of their local authority for more than 24 hours is known as a looked after child.";

export default function YouthProfileCard({
  child,
  compact = false,
  value,
  onChange,
}: YouthProfileCardProps) {
  const profileId = React.useId();
  const update = <K extends keyof YouthProfile>(
    key: K,
    nextValue: YouthProfile[K],
  ) => {
    onChange({ ...value, [key]: nextValue });
  };

  const renderYesNo = (
    label: string,
    key:
      | "isLookedAfterChild"
      | "knownToSocialServices"
      | "hasEHCP"
      | "underCAMHS",
    helpText?: string,
  ) => compact ? (
    <div className="family-consent-row youth-profile-support-row">
      <div id={`${profileId}-${key}-label`}>
        <FormFieldLabel component="span" label={label} helpText={helpText} />
      </div>
      <div className="family-consent-options" role="radiogroup"
        aria-labelledby={`${profileId}-${key}-label`}>
        {[true, false].map((answer) => (
          <label key={String(answer)}>
            <input type="radio" name={`${profileId}-${key}`}
              checked={value[key] === answer} onChange={() => update(key, answer)} />
            {answer ? "Yes" : "No"}
          </label>
        ))}
      </div>
    </div>
  ) : (
    <div className="form-group">
      <FormFieldLabel label={label} helpText={helpText} />
      <div className="button-row button-row-left" style={{ marginTop: 0 }}>
        <button
          type="button"
          className={`action-button compact ${
            value[key] === true ? "" : "secondary"
          }`}
          onClick={() => update(key, true)}
        >
          Yes
        </button>
        <button
          type="button"
          className={`action-button compact ${
            value[key] === false ? "" : "secondary"
          }`}
          onClick={() => update(key, false)}
        >
          No
        </button>
      </div>
      {value[key] === null && (
        <div className="field-hint">Select yes or no</div>
      )}
    </div>
  );

  const age = getAge(child.dateOfBirth);

  return (
    <div className={`card${compact ? " youth-profile-card--compact" : ""}`}>
      <div className="card-header">
        <div className="card-title">Profile</div>
        {!compact && <div className="field-hint">
          Youth profile details are captured for children added to this registration.
          <br />
          {getDisplayName(child)}
          {age !== null ? ` - Age ${age}` : ""}
        </div>}
      </div>

      <div className="form-label youth-profile-section-heading">Education</div>
      <div className="form-row">
        <div className="form-group">
          <label className="form-label" htmlFor={`${profileId}-schoolName`}>School / College</label>
          <input
            id={`${profileId}-schoolName`}
            className="form-input"
            value={value.schoolName}
            onChange={(e) => update("schoolName", e.target.value)}
          />
        </div>
        <div className="form-group">
          <label className="form-label" htmlFor={`${profileId}-schoolYear`}>School Year</label>
          <select
            id={`${profileId}-schoolYear`}
            className="form-input"
            value={value.schoolYear}
            onChange={(e) => update("schoolYear", e.target.value)}
          >
            <option value="">Select</option>
            {schoolYears.map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="form-label youth-profile-section-heading">Additional Support</div>
      <div className={compact ? "youth-profile-support-list" : "form-row"}>
        {renderYesNo(
          compact ? "Looked-after child" : "Is Child/Young Person Looked After?",
          "isLookedAfterChild",
          lookedAfterChildHelpText,
        )}
        {renderYesNo(
          compact ? "Known to social services" : "Is this young person known to social services?",
          "knownToSocialServices",
        )}
        {renderYesNo(
          compact ? "Education, Health and Care Plan (EHCP)" : "Does this young person have an Education Health Care Plan?",
          "hasEHCP",
        )}
        {renderYesNo(compact ? "Supported by CAMHS" : "Is this young person supported by CAMHS?", "underCAMHS")}
      </div>

      <div className="form-label youth-profile-section-heading">Health and needs</div>
      <div className="youth-profile-medical-fields">
      <div className="form-group">
        <label className="form-label" htmlFor={`${profileId}-medicalInformation`}>Medical Conditions</label>
        <textarea
          id={`${profileId}-medicalInformation`}
            className="form-input"
          rows={compact ? 2 : 3}
          value={value.medicalInformation}
          onChange={(e) => update("medicalInformation", e.target.value)}
        />
      </div>
      <div className="form-group">
        <label className="form-label" htmlFor={`${profileId}-additionalNeeds`}>Additional Needs</label>
        <textarea
          id={`${profileId}-additionalNeeds`}
            className="form-input"
          rows={compact ? 2 : 3}
          value={value.additionalNeeds}
          onChange={(e) => update("additionalNeeds", e.target.value)}
        />
      </div>
      <div className="form-group">
        <label className="form-label" htmlFor={`${profileId}-dietaryRequirements`}>Dietary Requirements</label>
        <textarea
          id={`${profileId}-dietaryRequirements`}
            className="form-input"
          rows={compact ? 2 : 3}
          value={value.dietaryRequirements}
          onChange={(e) => update("dietaryRequirements", e.target.value)}
        />
      </div>

      </div>
      <div className="form-label youth-profile-section-heading">GP</div>
      <div className="form-row">
        <div className="form-group">
          <label className="form-label" htmlFor={`${profileId}-gpName`}>GP Name</label>
          <input
            id={`${profileId}-gpName`}
            className="form-input"
            value={value.gpName}
            onChange={(e) => update("gpName", e.target.value)}
          />
        </div>
        <div className="form-group">
          <label className="form-label" htmlFor={`${profileId}-gpContactNumber`}>GP Contact Number</label>
          <input
            id={`${profileId}-gpContactNumber`}
            className="form-input"
            value={value.gpContactNumber}
            onChange={(e) => update("gpContactNumber", e.target.value)}
          />
        </div>
      </div>
    </div>
  );
}
